import { useEffect, useState } from "preact/hooks";
import type { FieldOf } from "@fobu/schema";
import { autocompleteFor } from "../../lib/autocomplete";
import { t } from "../../lib/i18n";
import type { FieldProps } from "./types";

type TextLike = FieldOf<"short_text" | "email" | "phone" | "url">;

const INPUT_TYPE: Record<TextLike["type"], string> = { short_text: "text", email: "email", phone: "tel", url: "url" };
const INPUT_MODE: Partial<Record<TextLike["type"], string>> = { email: "email", phone: "tel", url: "url" };

export function TextInput({ field, value, onChange, a11y }: FieldProps<TextLike>) {
  return (
    <input
      class="fb-input"
      id={a11y.id}
      type={INPUT_TYPE[field.type]}
      inputMode={INPUT_MODE[field.type] as never}
      value={typeof value === "string" ? value : ""}
      onInput={(e) => onChange(e.currentTarget.value)}
      placeholder={"placeholder" in field ? field.placeholder : undefined}
      maxLength={"maxLength" in field ? field.maxLength : undefined}
      autoComplete={autocompleteFor(field)}
      spellcheck={field.type === "short_text"}
      required={field.required}
      aria-required={field.required}
      aria-invalid={a11y.invalid || undefined}
      aria-describedby={a11y.describedBy}
    />
  );
}

export function LongText({ field, value, onChange, a11y, conversational }: FieldProps<FieldOf<"long_text">>) {
  const text = typeof value === "string" ? value : "";
  return (
    <>
      <textarea
        class="fb-input fb-textarea"
        id={a11y.id}
        rows={conversational ? 3 : 4}
        value={text}
        onInput={(e) => {
          onChange(e.currentTarget.value);
          // Automatisch mitwachsen
          const el = e.currentTarget;
          el.style.height = "auto";
          el.style.height = `${Math.min(el.scrollHeight, 480)}px`;
        }}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        required={field.required}
        aria-required={field.required}
        aria-invalid={a11y.invalid || undefined}
        aria-describedby={a11y.describedBy}
      />
      {field.maxLength !== undefined && (
        <p class="fb-counter" aria-live="polite" aria-atomic="true">
          {text.length >= field.maxLength * 0.8 ? `${text.length} / ${field.maxLength} Zeichen` : ""}
        </p>
      )}
    </>
  );
}

/** Deutsche und englische Dezimalschreibweise akzeptieren; ungültiger Text bleibt Text (→ Fehlermeldung). */
export function parseLocaleNumber(raw: string): number | string | undefined {
  const s = raw.trim().replace(/\s/g, "");
  if (s === "") return undefined;
  const normalized = s.includes(",") && !s.includes(".") ? s.replace(",", ".") : s.replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".");
  const n = Number(normalized);
  return /^-?\d*\.?\d+$/.test(normalized) && Number.isFinite(n) ? n : raw;
}

export function NumberInput({ field, value, onChange, a11y }: FieldProps<FieldOf<"number">>) {
  const fmt = (v: unknown) => (typeof v === "number" ? String(v).replace(".", ",") : typeof v === "string" ? v : "");
  const [raw, setRaw] = useState(fmt(value));
  useEffect(() => {
    // Externe Änderungen (Entwurf, Vorbelegung) übernehmen, ohne die Eingabe zu stören.
    if (parseLocaleNumber(raw) !== value) setRaw(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div class="fb-input-affix">
      <input
        class="fb-input"
        id={a11y.id}
        type="text"
        inputMode={field.decimals > 0 ? "decimal" : "numeric"}
        value={raw}
        onInput={(e) => {
          setRaw(e.currentTarget.value);
          onChange(parseLocaleNumber(e.currentTarget.value));
        }}
        autoComplete={autocompleteFor(field) ?? "off"}
        required={field.required}
        aria-required={field.required}
        aria-invalid={a11y.invalid || undefined}
        aria-describedby={a11y.describedBy}
      />
      {field.unit && <span class="fb-affix" aria-hidden="true">{field.unit}</span>}
    </div>
  );
}

export function DateTimeInput({ field, value, onChange, a11y }: FieldProps<FieldOf<"date" | "time" | "datetime">>) {
  const type = field.type === "datetime" ? "datetime-local" : field.type;
  const v = typeof value === "string" ? value : "";
  const input = (
    <input
      class="fb-input fb-input--date"
      id={a11y.id}
      type={type}
      value={v}
      onInput={(e) => onChange(e.currentTarget.value)}
      min={field.type === "date" ? field.minDate : undefined}
      max={field.type === "date" ? field.maxDate : undefined}
      autoComplete={autocompleteFor(field)}
      required={field.required}
      aria-required={field.required}
      aria-invalid={a11y.invalid || undefined}
      aria-describedby={a11y.describedBy}
    />
  );
  // Freiwillige Angabe wieder leeren können – iPhone/iPad setzen beim Antippen sofort das heutige Datum.
  if (field.required || !v) return input;
  return (
    <div class="fb-date-clear">
      {input}
      <button type="button" class="fb-linkbtn" onPointerDown={(e) => e.preventDefault()} onClick={() => onChange(undefined)}>
        {t.clearValue}
      </button>
    </div>
  );
}
